/**
 * The pillar sidebar ("New navigation", v3 mockups).
 *
 * Home and Inbox on top, then five pillars, then Log Explorer and Notifications at the
 * bottom (they span every pillar). A page's related pages are tabs on it (pillarTabs.js), not
 * sidebar rows: an entry with a `group` stays lit on every tab of that group.
 * Product module sections are not drawn in this layout.
 *
 * Every link is an existing route. Labels are `nav:` keys. Gates mirror the
 * classic rows they replace:
 *   gate: 'governance'  the classic Assessment group's canOpenCustomGovernance
 *   feature: 'X'        isFeatureEnabled('X') (Inbox: RECOMMENDATIONS)
 *   orgIds: [n]         preview pages limited to these organisations
 * Each pillar is one licence module (`module`, a saf3ai-auth-sdk modules.yaml slug):
 * the heading carries the lock when the org has not bought it (browse-but-locked:
 * the pillar still shows and its pages open on the lock screen).
 * A `{ headingKey }` row is a sub-heading inside a pillar (Security › Posture).
 */
import { groupRoutes } from './pillarTabs';

// The rows above the pillars wear a colour each, like the pillar tiles, so the folded rail
// reads as one set of coloured tiles with captions.
export const PILLAR_HOME = { to: '/agent-atlas/atlas', icon: 'home', labelKey: 'pillarItems.home', color: '#7B68EE' };
export const PILLAR_INBOX = { to: '/agent-atlas/inbox', icon: 'inbox', labelKey: 'pillarItems.inbox', feature: 'RECOMMENDATIONS', group: 'inbox', color: '#3987E5' };

/** The rows above the pillars. */
export const PILLAR_TOP = [PILLAR_HOME, PILLAR_INBOX];

/** The rows below the pillars (after Governance), drawn like the top rows. */
export const PILLAR_BOTTOM = [
  { to: '/agent-atlas/log-explorer', icon: 'list', labelKey: 'items.logExplorer', color: '#199E70' },
  { to: '/agent-atlas/alerts', icon: 'bell', labelKey: 'pillarItems.notifications', group: 'notifications', color: '#C98500' },
];

export const PILLARS = [
  {
    key: 'pillar-discovery',
    module: 'discovery',
    labelKey: 'pillars.discovery',
    icon: 'compass',
    color: '#3987E5',
    items: [
      // "AI Agents" (was AI Estate, 2026-10-27): agents & apps and their MCP servers, skills & tools.
      { to: '/agent-atlas/inventory', icon: 'grid', labelKey: 'pillarItems.aiAgents', group: 'estate' },
      { to: '/agent-atlas/users', icon: 'users', labelKey: 'pillarItems.usersIdentities' },
      // Endpoints (2026-10-27): the Endpoint Security dashboard (which sensors see AI on devices, the
      // tools, who uses them, what left), with Devices and Endpoint AI tools as its tabs.
      { to: '/endpoint-security', icon: 'monitor', labelKey: 'items.endpoints', group: 'endpoint' },
      { to: '/cloud/overview', icon: 'cloud', labelKey: 'pillarTabs.cloud', group: 'cloud' },
      { to: '/security-atlas/ai-bom', icon: 'layers', labelKey: 'pillarItems.aiBom', group: 'aiBom' },
    ],
  },
  {
    key: 'pillar-assessment',
    module: 'assessment',
    labelKey: 'pillars.assessment',
    icon: 'check-square',
    color: '#C98500',
    items: [
      // Assessment's overview (2026-10-26: the All findings list was retired).
      { to: '/agent-atlas/assessment', icon: 'grid', labelKey: 'pillarTabs.overview', gate: 'governance' },
      { to: '/agent-atlas/assessment/red-team', icon: 'shield', labelKey: 'pillarItems.redTeam', gate: 'governance', group: 'redTeam' },
      { to: '/agent-atlas/assessment/evaluations', icon: 'box', labelKey: 'pillarItems.modelEvaluation', gate: 'governance' },
      { to: '/sast/overview', icon: 'code', labelKey: 'pillarItems.codeSecurity', group: 'codeSecurity' },
    ],
  },
  {
    key: 'pillar-security',
    module: 'security',
    labelKey: 'pillars.security',
    icon: 'shield',
    color: '#D95926',
    items: [
      { to: '/security-atlas/overview', icon: 'shield', labelKey: 'pillarItems.threatSummary' },
      { to: '/security-atlas/signals', icon: 'activity', labelKey: 'pillarItems.detections', group: 'detections' },
      { to: '/security-atlas/incidents', icon: 'alert-triangle', labelKey: 'pillarItems.incidents', group: 'incidents' },
      { to: '/security-atlas/hunt', icon: 'search', labelKey: 'pillarItems.threatHunting' },
      { to: '/security-atlas/skills', icon: 'layers', labelKey: 'pillarItems.skillsWorkflows' },
      { headingKey: 'pillarItems.posture' },
      { to: '/security-atlas/mcp-security', icon: 'server', labelKey: 'pillarItems.mcpSecurity', orgIds: [3] },
      { to: '/security-atlas/agent-instructions', icon: 'file-text', labelKey: 'pillarItems.agentInstructions', orgIds: [3] },
      // Accepted risk and mutes are the SOC's decisions not to act; findings are Assessment's.
      { to: '/security-atlas/exceptions', icon: 'check-circle', labelKey: 'pillarItems.exceptions' },
    ],
  },
  {
    key: 'pillar-usage',
    module: 'finops',
    labelKey: 'pillars.usage',
    icon: 'pie-chart',
    color: '#199E70',
    items: [
      { to: '/agent-atlas/finops', icon: 'dollar-sign', labelKey: 'pillarItems.aiSpend', group: 'finops' },
      { to: '/agent-atlas/adoption/conversations', icon: 'trending-up', labelKey: 'pillarItems.aiAdoption', group: 'adoption' },
    ],
  },
  {
    key: 'pillar-governance',
    module: 'governance',
    labelKey: 'pillars.governance',
    icon: 'clipboard',
    color: '#7B68EE',
    items: [
      { to: '/agent-atlas/compliance', icon: 'shield', labelKey: 'items.compliance' },
      { to: '/agent-atlas/compliance-audit', icon: 'file-text', labelKey: 'items.auditReport' },
      { to: '/agent-atlas/policy', icon: 'lock', labelKey: 'pillarItems.policyAndGuardrails', group: 'policy' },
    ],
  },
];

/** Every route an entry lights up on: its own, and its tab group's. */
export const itemRoutes = (item) => (item.to ? [item.to, ...(item.group ? groupRoutes(item.group) : [])] : []);

/** Every route a pillar holds (for active-section highlighting). */
export const pillarRoutes = (pillar) => pillar.items.flatMap(itemRoutes);

const pathOf = (to) => String(to).split('?')[0];
const underAny = (path, prefixes) => prefixes.some((p) => path === p || path.startsWith(p));

/**
 * Does a lens (a persona, or a use case chosen in the classic menu) keep this pillar entry?
 * Yes when one of the lens's pages is the entry's page or one of its tabs (a page reached only
 * through a tab, like Sensitive data under Detections, keeps the entry that holds it), or when the
 * entry's own page sits in a classic section the lens shows whole (Code Security for `sast`).
 * Only the entry's own page is checked against sections: a tab borrowed from another section
 * (Incidents' Approval settings is a Management page) would otherwise keep it in every lens.
 *
 * `pages`: a Set of the lens's page paths; `sectionRoutes(key)`: that section's route prefixes.
 */
export const lensKeepsItem = (item, { pages, sections, sectionRoutes }) => {
  if (!item.to) return false;
  if (itemRoutes(item).some((r) => pages.has(pathOf(r)))) return true;
  return sections.some((key) => underAny(pathOf(item.to), sectionRoutes(key) || []));
};

/**
 * The pillars with only the entries `keep` passes. A sub-heading stays only above an entry it
 * still heads, and a pillar left with no entry is dropped (its heading would head nothing).
 */
export const trimPillars = (pillars, keep) => pillars
  .map((p) => {
    const items = p.items.filter((it) => it.headingKey || keep(it));
    return {
      ...p,
      items: items.filter((it, i) => !it.headingKey || (items[i + 1] && !items[i + 1].headingKey)),
    };
  })
  .filter((p) => p.items.some((it) => !it.headingKey));

/** Keep an item when its gates pass. */
export const passesGates = (item, { canGovern, orgId, featureOn }) => {
  if (item.gate === 'governance' && !canGovern) return false;
  if (item.orgIds && !item.orgIds.includes(orgId)) return false;
  if (item.feature && !featureOn(item.feature)) return false;
  return true;
};
